// src/modules/app-views/products/pages/Document/tabs/AiInfoTab/ManufacturerSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Descriptions, Collapse, theme } from 'antd';
import { BankOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// HELPERS
// *******************************************************************************
// Safely render value that might be an object {uid, label} or a string
function renderValue(value) {
  if (!value) return null;
  if (typeof value === 'object' && value.label) return value.label;
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
}

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ManufacturerSection = ({ data, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const mf = data?.manufacturer;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasData = mf && (mf.name || mf.tradeName || mf.website || mf.productPage || mf.country);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) return null;

  return (
    <Collapse
      className="collapsed-content-fit"
      collapsible="icon"
      defaultActiveKey={['manufacturer']}
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'manufacturer',
          label: (
            <Flex align="center" gap={8}>
              <BankOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontWeight: 600 }}>{translateX('manufacturer')}</span>
            </Flex>
          ),
          showArrow: false,
          children: (
            <Descriptions
              bordered
              size="small"
              column={1}
              layout={isMobile ? 'vertical' : 'horizontal'}
              styles={{ label: { fontWeight: 500 } }}
            >
              {mf.name && (
                <Descriptions.Item label={translateX('name')}>
                  {renderValue(mf.name)}
                </Descriptions.Item>
              )}
              {mf.tradeName && (
                <Descriptions.Item label={translateX('trade_name')}>
                  {renderValue(mf.tradeName)}
                </Descriptions.Item>
              )}
              {mf.website && (
                <Descriptions.Item label={translateX('website')}>
                  <a href={renderValue(mf.website)} target="_blank" rel="noopener noreferrer">
                    {renderValue(mf.website)}
                  </a>
                </Descriptions.Item>
              )}
              {mf.productPage && (
                <Descriptions.Item label={translateX('product_page')}>
                  <a href={renderValue(mf.productPage)} target="_blank" rel="noopener noreferrer">
                    {renderValue(mf.productPage)}
                  </a>
                </Descriptions.Item>
              )}
              {mf.country && (
                <Descriptions.Item label={translateX('country')}>
                  {renderValue(mf.country)}
                </Descriptions.Item>
              )}
            </Descriptions>
          ),
        },
      ]}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ManufacturerSection;
